import { Container, Typography } from '@cleartrip/ct-design-components'
import React, { memo } from 'react'
import type { ReactNode } from 'react'

import {
  getIsDynamicContentLoading,
  getProgressiveLoadingIndices,
  getWowFares,
  getWowLoadingFareIds,
} from '@/redux/slices/srpSlice'
import { useAppSelector } from '@/redux/store/hooks'

import { Stencil } from '@/components/common/Stencil'
import WowFareCalloutShared from '@/components/wowPricingComponents/WowFareCallout'
import { useWowPricingExperiment } from '@/hooks/srp/useWowPricingExperiment'
import { isUserSignedIn } from '@/utils/general/user'

export interface OfferCalloutProps {
  fareId: string
  couponCallout?: string
  couponColor?: string
  showPerAdult?: boolean
  cardIndex?: number
  trailing?: ReactNode
}

const withTrailing = (content: ReactNode, trailing?: ReactNode) => {
  if (!trailing) return content
  return (
    <Container className='flex flex-middle flex-no-shrink'>
      {content}
      {trailing}
    </Container>
  )
}

const StaticCoupon: React.FC<{ message: string; color?: string }> = ({ message, color }) => (
  <Typography
    className='ws-nowrap ta-right fw-500'
    color={color ? undefined : 'success'}
    componentNode='span'
    style={color ? { color } : undefined}
    variant='B3'
  >
    {message}
  </Typography>
)

const OfferCallout: React.FC<OfferCalloutProps> = ({
  fareId,
  couponCallout,
  couponColor,
  showPerAdult = false,
  cardIndex,
  trailing,
}) => {
  const isLoggedIn = isUserSignedIn()
  const { wowOn } = useWowPricingExperiment()
  const wowFares = useAppSelector(getWowFares)
  const isDynamicContentLoading = useAppSelector(getIsDynamicContentLoading)
  const wowLoadingFareIds = useAppSelector(getWowLoadingFareIds)
  const progressiveLoadingIndices = useAppSelector(getProgressiveLoadingIndices)

  const isLoadingForFare =
    isDynamicContentLoading ||
    (typeof cardIndex === 'number' &&
      Boolean(progressiveLoadingIndices?.includes(cardIndex))) ||
    (Boolean(fareId) && Boolean(wowLoadingFareIds?.includes(fareId)))

  if (isLoggedIn && wowOn) {
    if (isLoadingForFare) {
      return withTrailing(<Stencil shimmer borderRadius={3} height={16} width={180} />, trailing)
    }

    const fare = wowFares?.[fareId]
    const pricing = showPerAdult ? fare?.pricing?.perAdultPricing : fare?.pricing?.totalPricing
    const { couponDetails, wowApplicable = false } = pricing || {}
    const { message = '', wowDisplayPrice = 0 } = couponDetails || {}

    if (wowApplicable || message) {
      const processedMessage = message
        ? message.replace('{couponCode}', couponDetails?.couponCode || '')
        : ''
      if (processedMessage) {
        return withTrailing(
          <WowFareCalloutShared
            couponCallout={processedMessage}
            isSrpRevamp
            wowApplicable={Boolean(wowApplicable)}
            wowDisplayPrice={wowDisplayPrice}
          />,
          trailing
        )
      }
    }
    return null
  }

  if (couponCallout) {
    return withTrailing(<StaticCoupon color={couponColor} message={couponCallout} />, trailing)
  }

  return null
}

export default memo(OfferCallout)
